import React, { useState, useEffect, useLayoutEffect, useRef, useMemo } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { useGeneral } from '../../../contexts';
import { useUnsavedChanges } from '../../../contexts/UnsavedChangesContext';
import Services from '../../../services/Services';
import Button from '../../../components/ui/Button/Button';
import LoadingSkeleton from '../../../components/ui/LoadingSkeleton/LoadingSkeleton';
import ImageUploadModal from '../../../components/ui/ImageUploadModal/ImageUploadModal';
import Modal from '../../../components/ui/Modal/Modal';
import { Tabs, Tab } from '../../../components/ui/Tabs';
import transitionStyles from '../../../styles/transitions.module.css';
import formStyles from '../../../components/ui/Form/Form.module.css';
import { CoverFile, Channel } from '../../../types';
import { convertFileToBase64 } from '../../../utils/fileConversion';
import styles from './CommunitySettingsPage.module.css';

import { OrganizerPanelOutletContext } from '../../../types/organizerPanel.types';

const CommunitySettingsPage: React.FC = () => {
  const { channel, refreshChannel } = useOutletContext<OrganizerPanelOutletContext>();
  const { auth, language, toastSuccess, toastError } = useGeneral();
  const { token } = auth;
  const { getTranslation } = language;
  const navigate = useNavigate();
  const { setHasUnsavedChanges, pendingNavigation, clearPendingNavigation } = useUnsavedChanges();

  const [activeTab, setActiveTab] = useState<'general' | 'social' | 'payment' | 'danger'>('general');
  const initialLoading = false;
  const [saving, setSaving] = useState(false);
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const [showBannerModal, setShowBannerModal] = useState(false);
  const [showLogoModal, setShowLogoModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Helper to get initial values from channel
  const getInitialValues = (ch: Channel | null) => {
    if (!ch) {
      return {
        channelTitle: '',
        description: '',
        isPublic: false,
        instagram: '',
        website: '',
        x: '',
        linkedin: '',
        linkedinURL: '',
        iban: '',
        bankAccountHolder: '',
        bankAccountNo: '',
      };
    }
    const links = Array.isArray(ch.links) ? {} : (ch.links as { instagram?: string; website?: string; x?: string; linkedin?: string; linkedinURL?: string } | undefined);
    return {
      channelTitle: ch.channelTitle || ch.title || '',
      description: ch.description || '',
      isPublic: ch.isPublic === "1",
      instagram: links?.instagram || '',
      website: links?.website || '',
      x: links?.x || '',
      linkedin: links?.linkedin || '',
      linkedinURL: links?.linkedinURL || '',
      iban: ch.iban || '',
      bankAccountHolder: ch.bank_account_holder || '',
      bankAccountNo: ch.bank_account_no || '',
    };
  };

  // Initial values for comparison - use refs to persist across renders
  const initialValuesRef = useRef(getInitialValues(channel));

  // Form state - initialize with channel values
  const [channelTitle, setChannelTitle] = useState(channel?.channelTitle || channel?.title || '');
  const [description, setDescription] = useState(channel?.description || '');
  const channelLinks = Array.isArray(channel?.links) ? {} : (channel?.links as { instagram?: string; website?: string; x?: string; linkedin?: string; linkedinURL?: string } | undefined);
  const [isPublic, setIsPublic] = useState(channel?.isPublic === "1");
  const [instagram, setInstagram] = useState(channelLinks?.instagram || '');
  const [website, setWebsite] = useState(channelLinks?.website || '');
  const [x, setX] = useState(channelLinks?.x || '');
  const [linkedin, setLinkedin] = useState(channelLinks?.linkedin || '');
  const [linkedinURL, setLinkedinURL] = useState(channelLinks?.linkedinURL || '');
  const [iban, setIban] = useState(channel?.iban || '');
  const [bankAccountHolder, setBankAccountHolder] = useState(channel?.bank_account_holder || '');
  const [bankAccountNo, setBankAccountNo] = useState(channel?.bank_account_no || '');

  // Track the channel ID to detect when we switch to a different channel
  const channelIdRef = useRef<string | null>(null);
  const isInitializedRef = useRef(false);
  const justSavedRef = useRef(false);

  // Update initial values and form state when channel loads or changes
  useEffect(() => {
    if (channel) {
      const currentChannelId = channel.channelID || channel.id || null;
      const initial = getInitialValues(channel);
      
      // Only reset if this is a different channel or first initialization
      if (channelIdRef.current !== currentChannelId || !isInitializedRef.current) {
        channelIdRef.current = currentChannelId;
        isInitializedRef.current = true;
        initialValuesRef.current = initial;
        // Reset form state to match initial values to ensure they're in sync
        setChannelTitle(initial.channelTitle);
        setDescription(initial.description);
        setIsPublic(initial.isPublic);
        setInstagram(initial.instagram);
        setWebsite(initial.website);
        setX(initial.x);
        setLinkedin(initial.linkedin);
        setLinkedinURL(initial.linkedinURL);
        setIban(initial.iban);
        setBankAccountHolder(initial.bankAccountHolder);
        setBankAccountNo(initial.bankAccountNo);
        // Clear file states when channel changes
        setBannerFile(null);
        setLogoFile(null);
      } else {
        // Same channel, just update initial values ref if needed (shouldn't happen often)
        // This handles the case where channel object reference changes but it's the same channel
        const currentInitial = initialValuesRef.current;
        if (
          initial.channelTitle !== currentInitial.channelTitle ||
          initial.description !== currentInitial.description ||
          initial.isPublic !== currentInitial.isPublic ||
          initial.instagram !== currentInitial.instagram ||
          initial.website !== currentInitial.website ||
          initial.x !== currentInitial.x ||
          initial.linkedin !== currentInitial.linkedin ||
          initial.linkedinURL !== currentInitial.linkedinURL ||
          initial.iban !== currentInitial.iban ||
          initial.bankAccountHolder !== currentInitial.bankAccountHolder ||
          initial.bankAccountNo !== currentInitial.bankAccountNo
        ) {
          initialValuesRef.current = initial;
        }
      }
    }
  }, [channel]);

  // Image state
  const [bannerImage, setBannerImage] = useState<string | null>(channel?.topBanner || null);
  const [logoImage, setLogoImage] = useState<string | null>(channel?.channelLogo || channel?.logo || null);
  const [bannerFile, setBannerFile] = useState<CoverFile | null>(null);
  const [logoFile, setLogoFile] = useState<CoverFile | null>(null);

  // Check if there are unsaved changes
  // Only check after initialization to avoid false positives
  const hasUnsavedChanges = useMemo(() => {
    if (!isInitializedRef.current) {
      return false; // Don't report changes until initialized
    }
    const initial = initialValuesRef.current;
    const hasChanges = (
      channelTitle !== initial.channelTitle ||
      description !== initial.description ||
      isPublic !== initial.isPublic ||
      instagram !== initial.instagram ||
      website !== initial.website ||
      x !== initial.x ||
      linkedin !== initial.linkedin ||
      linkedinURL !== initial.linkedinURL ||
      iban !== initial.iban ||
      bankAccountHolder !== initial.bankAccountHolder ||
      bankAccountNo !== initial.bankAccountNo ||
      bannerFile !== null ||
      logoFile !== null
    );
    return hasChanges;
  }, [channelTitle, description, isPublic, instagram, website, x, linkedin, linkedinURL, iban, bankAccountHolder, bankAccountNo, bannerFile, logoFile]);

  // Handle tab change - no modal needed, tabs share same state
  const handleTabChange = (newTab: 'general' | 'social' | 'payment' | 'danger') => {
    setActiveTab(newTab);
  };

  // Update unsaved changes context synchronously using useLayoutEffect to avoid timing issues
  useLayoutEffect(() => {
    setHasUnsavedChanges(hasUnsavedChanges);
  }, [hasUnsavedChanges, setHasUnsavedChanges]);

  // Show modal when navigation is blocked - verify hasUnsavedChanges from component state
  useEffect(() => {
    if (pendingNavigation && hasUnsavedChanges) {
      setShowUnsavedModal(true);
    } else if (!pendingNavigation) {
      setShowUnsavedModal(false);
    }
  }, [pendingNavigation, hasUnsavedChanges]);

  // Handle navigation away from page (browser refresh/close)
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  const handleBannerUpload = async (file: File) => {
    try {
      const coverFile = await convertFileToBase64(file);
      setBannerFile(coverFile);
      const preview = URL.createObjectURL(file);
      setBannerImage(preview);
    } catch {
      // Banner conversion failed
    }
  };

  const handleLogoUpload = async (file: File) => {
    try {
      const coverFile = await convertFileToBase64(file);
      setLogoFile(coverFile);
      const preview = URL.createObjectURL(file);
      setLogoImage(preview);
    } catch {
      // Logo conversion failed
    }
  };

  const handleSave = async () => {
    if (!token || !channel?.channelID) return;

    // Check if there are any changes to save
    if (!hasUnsavedChanges) {
      toastSuccess(getTranslation('organizerPanel.noChangesMade') || 'Değişiklik yapılmadı');
      return;
    }

    setSaving(true);
    try {
      const response = await Services.SocialHiveUpdateChannel(
        token,
        channel.channelID,
        channelTitle,
        channelTitle,
        description,
        logoFile,
        bannerFile,
        isPublic,
        channel.isFree === "1",
        {
          instagram: instagram || undefined,
          website: website || undefined,
          x: x || undefined,
          linkedin: linkedin || undefined,
          linkedinURL: linkedinURL || undefined,
        },
        iban || undefined,
        bankAccountHolder || undefined,
        bankAccountNo || undefined
      );
      
      // Show success message
      toastSuccess(getTranslation('organizerPanel.settingsSaved') || 'Ayarlar kaydedildi');
      
      // Reset file states
      setBannerFile(null);
      setLogoFile(null);
      
      // Mark that we just saved to prevent useEffect from resetting form state
      justSavedRef.current = true;
      
      // Update initial values to current values (what was just saved)
      initialValuesRef.current = {
        channelTitle,
        description,
        isPublic,
        instagram,
        website,
        x,
        linkedin,
        linkedinURL,
        iban,
        bankAccountHolder,
        bankAccountNo,
      };
      
      // If response contains updated channel data, update images
      if (response.data.result) {
        const updatedChannel = response.data.result;
        if (updatedChannel.topBanner) {
          setBannerImage(updatedChannel.topBanner);
        }
        if (updatedChannel.channelLogo || updatedChannel.logo) {
          setLogoImage(updatedChannel.channelLogo || updatedChannel.logo);
        }
      }
      
      // Trigger channel refetch to update the channel object in outlet context
      if (refreshChannel) {
        refreshChannel();
      }
    } catch (error) {
      toastError(getTranslation('organizerPanel.settingsSaveError') || 'Ayarlar kaydedilemedi');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteChannel = async () => {
    if (!token || !channel?.channelID) return;

    setDeleting(true);
    try {
      const response = await Services.SocialHiveDeleteChannel(token, channel.channelID);
      if (response.data.status) {
        toastSuccess(getTranslation('organizerPanel.channelDeleted') || 'Topluluk başarıyla silindi');
        // Redirect to communities page
        navigate('/communities', { replace: true });
      } else {
        toastError(response.data.message || getTranslation('organizerPanel.deleteChannelError') || 'Topluluk silinemedi');
        setDeleting(false);
        setShowDeleteModal(false);
      }
    } catch (error: any) {
      toastError(error.response?.data?.message || getTranslation('organizerPanel.deleteChannelError') || 'Topluluk silinemedi');
      setDeleting(false);
      setShowDeleteModal(false);
    }
  };

  const handleUnsavedModalClose = () => {
    setShowUnsavedModal(false);
    clearPendingNavigation();
  };

  const handleUnsavedModalDiscard = () => {
    setShowUnsavedModal(false);
    if (pendingNavigation) {
      pendingNavigation();
    }
    clearPendingNavigation();
  };

  const handleUnsavedModalSave = async () => {
    await handleSave();
    setShowUnsavedModal(false);
    if (pendingNavigation) {
      pendingNavigation();
    }
    clearPendingNavigation();
  };

  return (
    <div className={styles.communitySettingsPage}>
      {initialLoading ? (
        <div className={styles.content}>
          <div className={styles.header}>
            <LoadingSkeleton height={32} width="200px" />
          </div>
          <div className={styles.tabContent}>
            <div style={{ marginBottom: '24px' }}>
              <LoadingSkeleton height={300} width="100%" />
            </div>
            <div style={{ marginBottom: '16px' }}>
              <LoadingSkeleton height={40} width="100%" />
            </div>
            <div style={{ marginBottom: '16px' }}>
              <LoadingSkeleton height={40} width="100%" />
            </div>
            <LoadingSkeleton height={100} width="100%" />
          </div>
        </div>
      ) : (
        <>
      <div className={styles.header}>
        <Tabs variant="default">
          <Tab
            variant="default"
            isActive={activeTab === 'general'}
            onClick={() => handleTabChange('general')}
          >
            {getTranslation('organizerPanel.general') || 'General'}
          </Tab>
          <Tab
            variant="default"
            isActive={activeTab === 'social'}
            onClick={() => handleTabChange('social')}
          >
            {getTranslation('organizerPanel.socialMedia') || 'Social Media'}
          </Tab>
          <Tab
            variant="default"
            isActive={activeTab === 'payment'}
            onClick={() => handleTabChange('payment')}
          >
            {getTranslation('organizerPanel.payment') || 'Ödeme'}
          </Tab>
          <Tab
            variant="default"
            danger
            isActive={activeTab === 'danger'}
            onClick={() => handleTabChange('danger')}
          >
            {getTranslation('organizerPanel.dangerZone') || 'Danger Zone'}
          </Tab>
        </Tabs>
        <Button
          variant="primary"
          size="medium"
          onClick={handleSave}
          disabled={saving}
          className={styles.saveButton}
        >
          {saving 
            ? (getTranslation('common.saving') || 'Kaydediliyor...')
            : (getTranslation('organizerPanel.saveChanges') || 'Değişiklikleri Kaydet')
          }
        </Button>
      </div>

      <div className={styles.content} key={activeTab}>
        <div className={transitionStyles.fadeIn}>
        {activeTab === 'general' ? (
          <div className={styles.generalSettings}>
            {/* Image Uploads */}
            <div className={styles.imageUploads}>
              <div className={styles.bannerUpload}>
                <div
                  className={styles.bannerPlaceholder}
                  onClick={() => setShowBannerModal(true)}
                >
                  {bannerImage ? (
                    <img src={bannerImage} alt={getTranslation('organizerPanel.bannerImage') || 'Banner'} className={styles.bannerImage} />
                  ) : (
                    <div className={styles.uploadIcon}>
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 5V19M5 12H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                  )}
                </div>
              </div>

              <div className={styles.logoUpload}>
                <div
                  className={styles.logoPlaceholder}
                  onClick={() => setShowLogoModal(true)}
                >
                  {logoImage ? (
                    <img src={logoImage} alt={getTranslation('organizerPanel.logoImage') || 'Logo'} className={styles.logoImage} />
                  ) : (
                    <div className={styles.uploadIcon}>
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 5V19M5 12H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Form Fields */}
            <div className={formStyles.formFields}>
              <div className={formStyles.formField}>
                <label className={styles.label}>
                  {getTranslation('organizerPanel.communityName') || 'Topluluk adı'} *
                </label>
                <input
                  type="text"
                  value={channelTitle}
                  onChange={(e) => setChannelTitle(e.target.value)}
                  className={styles.input}
                  placeholder={getTranslation('organizerPanel.communityNamePlaceholder') || 'Topluluk adını girin'}
                />
              </div>

              <div className={styles.formField}>
                <label className={styles.label}>
                  {getTranslation('organizerPanel.communityDescription') || 'Topluluk Açıklaması'} *
                </label>
                <div className={styles.textareaWrapper}>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    className={styles.textarea}
                    rows={6}
                    placeholder={getTranslation('organizerPanel.communityDescriptionPlaceholder') || 'Açıklama girin'}
                  />
                </div>
              </div>
            </div>
          </div>
        ) : activeTab === 'social' ? (
          <div className={styles.socialSettings}>
            <div className={formStyles.formFields}>
              <div className={formStyles.formField}>
                <label className={styles.label}>
                  {getTranslation('organizerPanel.instagram') || 'Instagram'}
                </label>
                <input
                  type="url"
                  value={instagram}
                  onChange={(e) => setInstagram(e.target.value)}
                  className={styles.input}
                  placeholder={getTranslation('organizerPanel.instagramPlaceholder') || 'https://instagram.com/...'}
                />
              </div>

              <div className={formStyles.formField}>
                <label className={styles.label}>
                  {getTranslation('organizerPanel.website') || 'Website'}
                </label>
                <input
                  type="url"
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                  className={styles.input}
                  placeholder={getTranslation('organizerPanel.websitePlaceholder') || 'https://...'}
                />
              </div>

              <div className={formStyles.formField}>
                <label className={styles.label}>
                  {getTranslation('organizerPanel.xTwitter') || 'X (Twitter)'}
                </label>
                <input
                  type="url"
                  value={x}
                  onChange={(e) => setX(e.target.value)}
                  className={styles.input}
                  placeholder={getTranslation('organizerPanel.xPlaceholder') || 'https://x.com/...'}
                />
              </div>

              <div className={formStyles.formField}>
                <label className={styles.label}>
                  {getTranslation('organizerPanel.linkedinUrl') || 'LinkedIn'}
                </label>
                <input
                  type="url"
                  value={linkedinURL}
                  onChange={(e) => setLinkedinURL(e.target.value)}
                  className={styles.input}
                  placeholder={getTranslation('organizerPanel.linkedinUrlPlaceholder') || 'https://linkedin.com/company/...'}
                />
              </div>

              <div className={formStyles.formField}>
                <label className={styles.labelWithTooltip}>
                  <span>{getTranslation('organizerPanel.linkedinCompanyId') || 'LinkedIn Company ID'}</span>
                  <span className={styles.tooltipWrapper}>
                    <svg className={styles.tooltipIcon} width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                      <path d="M12 16V12M12 8H12.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <span className={styles.tooltipText}>
                      {getTranslation('organizerPanel.linkedinCompanyIdTooltip') || 'This ID is found in the URL of the company LinkedIn page. It is recommended to add this as it will be used to display your organization as the issuer when adding certificates to LinkedIn.'}
                    </span>
                  </span>
                </label>
                <input
                  type="text"
                  value={linkedin}
                  onChange={(e) => {
                    const numericValue = e.target.value.replace(/[^0-9]/g, '');
                    setLinkedin(numericValue);
                  }}
                  className={styles.input}
                  placeholder="123456789"
                />
              </div>
            </div>
          </div>
        ) : activeTab === 'payment' ? (
          <div className={styles.socialSettings}>
            <div className={formStyles.formFields}>
              <div className={formStyles.formField}>
                <label className={styles.label}>
                  {getTranslation('organizerPanel.bankAccountHolder') || 'Hesap Sahibi'}
                </label>
                <input
                  type="text"
                  value={bankAccountHolder}
                  onChange={(e) => setBankAccountHolder(e.target.value)}
                  className={styles.input}
                  placeholder={getTranslation('organizerPanel.bankAccountHolderPlaceholder') || 'Hesap sahibi adını girin'}
                />
              </div>

              <div className={formStyles.formField}>
                <label className={styles.label}>
                  {getTranslation('organizerPanel.iban') || 'IBAN'}
                </label>
                <input
                  type="text"
                  value={iban}
                  onChange={(e) => setIban(e.target.value)}
                  className={styles.input}
                  placeholder={getTranslation('organizerPanel.ibanPlaceholder') || 'Örn: TR33 0006 1001 0000 0013 9000 015'}
                />
              </div>

              <div className={formStyles.formField}>
                <label className={styles.label}>
                  {getTranslation('organizerPanel.bankAccountNo') || 'Hesap Numarası'}
                </label>
                <input
                  type="text"
                  value={bankAccountNo}
                  onChange={(e) => setBankAccountNo(e.target.value)}
                  className={styles.input}
                  placeholder={getTranslation('organizerPanel.bankAccountNoPlaceholder') || 'Hesap numarasını girin'}
                />
              </div>
            </div>
          </div>
        ) : (
          <div className={styles.dangerZone}>
            <div className={styles.dangerSection}>
              <h3 className={styles.dangerTitle}>
                {getTranslation('organizerPanel.privateStatus') || 'Gizlilik Durumu'}
              </h3>
              <p className={styles.dangerDescription}>
                {getTranslation('organizerPanel.privateStatusDescription') || 
                  'Topluluğunuzun görünürlüğünü ayarlayın. Özel topluluklar sadece davet ile erişilebilir.'}
              </p>
              <div className={styles.toggleWrapper}>
                <label className={styles.toggleLabel}>
                  <input
                    type="checkbox"
                    checked={isPublic}
                    onChange={(e) => setIsPublic(e.target.checked)}
                    className={styles.toggle}
                  />
                  <span className={styles.toggleText}>
                    {isPublic 
                      ? (getTranslation('organizerPanel.public') || 'Herkese Açık')
                      : (getTranslation('organizerPanel.private') || 'Özel')
                    }
                  </span>
                </label>
              </div>
            </div>

            <div className={styles.dangerSection}>
              <h3 className={styles.dangerTitle}>
                {getTranslation('organizerPanel.deleteChannel') || 'Topluluğu Sil'}
              </h3>
              <p className={styles.dangerDescription}>
                {getTranslation('organizerPanel.deleteChannelDescription') || 
                  'Bu işlem geri alınamaz. Tüm etkinlikler, üyeler ve veriler kalıcı olarak silinecektir.'}
              </p>
              <Button
                variant="secondary"
                size="medium"
                onClick={() => setShowDeleteModal(true)}
                className={styles.deleteButton}
                disabled={deleting}
              >
                {deleting 
                  ? (getTranslation('common.deleting') || 'Siliniyor...')
                  : (getTranslation('organizerPanel.deleteChannel') || 'Topluluğu Sil')
                }
              </Button>
            </div>
          </div>
        )}
        </div>
      </div>

      {/* Unsaved Changes Modal */}
      {showUnsavedModal && (
        <div className={styles.modalOverlay} onClick={handleUnsavedModalClose}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <div className={styles.modalHeaderSpacer}></div>
              <h3 className={styles.modalTitle}>
                {getTranslation('organizerPanel.unsavedChanges') || 'Kaydedilmemiş Değişiklikler'}
              </h3>
              <button
                className={styles.modalCloseButton}
                onClick={handleUnsavedModalClose}
                aria-label="Close"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
            <p className={styles.modalMessage}>
              {getTranslation('organizerPanel.unsavedChangesMessage') || 
                'Kaydedilmemiş değişiklikleriniz var. Değişiklikleri kaydetmeden devam etmek istediğinizden emin misiniz?'}
            </p>
            <div className={styles.modalActions}>
              <Button
                variant="primary"
                size="medium"
                onClick={handleUnsavedModalSave}
                className={styles.modalActionButton}
              >
                {getTranslation('organizerPanel.saveChanges') || 'Değişiklikleri Kaydet'}
              </Button>
              <Button
                variant="secondary"
                size="medium"
                onClick={handleUnsavedModalDiscard}
                className={`${styles.discardButton} ${styles.modalActionButton}`}
              >
                {getTranslation('organizerPanel.discardChanges') || 'Değişiklikleri İptal Et'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Banner Upload Modal */}
      <ImageUploadModal
        isOpen={showBannerModal}
        onClose={() => setShowBannerModal(false)}
        onUpload={handleBannerUpload}
        title={getTranslation('organizerPanel.bannerImage') || 'Banner Görseli'}
        aspectRatio="3:1"
        recommendedSize="1200x400px"
        allowedTypes={['jpg', 'jpeg', 'png']}
        getTranslation={getTranslation}
      />

      {/* Logo Upload Modal */}
      <ImageUploadModal
        isOpen={showLogoModal}
        onClose={() => setShowLogoModal(false)}
        onUpload={handleLogoUpload}
        title={getTranslation('organizerPanel.logoImage') || 'Logo Görseli'}
        aspectRatio="1:1"
        recommendedSize="100x100px"
        allowedTypes={['jpg', 'jpeg', 'png']}
        getTranslation={getTranslation}
      />

      {/* Delete Channel Confirmation Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title={getTranslation('organizerPanel.deleteChannel') || 'Topluluğu Sil'}
        variant="confirm"
        message={getTranslation('organizerPanel.confirmDeleteChannel') || 'Bu topluluğu silmek istediğinizden emin misiniz? Bu işlem geri alınamaz. Tüm etkinlikler, üyeler ve veriler kalıcı olarak silinecektir.'}
        confirmText={getTranslation('organizerPanel.delete') || 'Sil'}
        cancelText={getTranslation('common.cancel') || 'İptal'}
        onConfirm={handleDeleteChannel}
        onCancel={() => setShowDeleteModal(false)}
        isDanger={true}
      />
        </>
      )}
    </div>
  );
};

export default CommunitySettingsPage;
